/**
 * SSE cursor and replay semantics (§16.4, ADR-107, AC-032).
 *
 * The rule this module exists to enforce: **a gap must never be visualised as
 * fact**. After losing a connection a client either replays from its last
 * cursor, or it is told the cursor is too old and given an atomic snapshot with
 * a fresh cursor — in which case it must *reset* the scene rather than continue
 * animating from a frame it can no longer justify.
 *
 * The failure this prevents is subtle and would be easy to ship: a client
 * reconnects, misses fifty events, and smoothly animates a character from where
 * it used to be to where it is now — inventing a journey that never happened.
 */
import { decodeCursor, encodeCursor } from '@autonomous-team/domain';
/**
 * Decides how to resume a dropped stream.
 *
 * Every branch that cannot guarantee a complete, ordered replay sets
 * `resetRequired`. There is deliberately no "probably fine, carry on" path.
 */
export function decideResume(lastEventId, window) {
    if (lastEventId === null || lastEventId.length === 0) {
        return {
            mode: 'cold_start',
            fromLedgerSequence: null,
            reason: 'no cursor supplied; sending a snapshot',
            resetRequired: true,
        };
    }
    const cursor = decodeCursor(lastEventId);
    if (cursor === null) {
        return {
            mode: 'snapshot_reset',
            fromLedgerSequence: null,
            reason: 'cursor could not be decoded; sending a snapshot rather than guessing a position',
            resetRequired: true,
        };
    }
    if (cursor.ledgerSequence > window.latestSequence) {
        // The client claims to have seen further than we have. Most likely the
        // control plane was restored from a backup. Trusting the client here would
        // silently skip everything between.
        return {
            mode: 'snapshot_reset',
            fromLedgerSequence: null,
            reason: 'cursor is ahead of the ledger; the client cannot be resumed safely',
            resetRequired: true,
        };
    }
    if (cursor.ledgerSequence < window.oldestRetainedSequence) {
        return {
            mode: 'snapshot_reset',
            fromLedgerSequence: null,
            reason: 'cursor has aged out of the replay window',
            resetRequired: true,
        };
    }
    const backlog = window.latestSequence - cursor.ledgerSequence;
    if (backlog > window.maxReplayEvents) {
        return {
            mode: 'snapshot_reset',
            fromLedgerSequence: null,
            reason: `backlog of ${backlog} events exceeds the replay limit of ${window.maxReplayEvents}`,
            resetRequired: true,
        };
    }
    return {
        mode: 'replay',
        fromLedgerSequence: cursor.ledgerSequence,
        reason: `replaying ${backlog} event(s) from sequence ${cursor.ledgerSequence}`,
        resetRequired: false,
    };
}
export function frameToSse(frame) {
    switch (frame.kind) {
        case 'snapshot':
            return `id: ${frame.cursor}\nevent: snapshot\ndata: ${JSON.stringify({ resetRequired: true, payload: frame.payload })}\n\n`;
        case 'event':
            return `id: ${frame.cursor}\nevent: ${frame.eventType}\ndata: ${JSON.stringify(frame.payload)}\n\n`;
        case 'heartbeat':
            // A comment frame keeps proxies from closing an idle connection without
            // being mistaken for data.
            return `: heartbeat\n\n`;
        case 'reset_required':
            return `event: reset_required\ndata: ${JSON.stringify({ reason: frame.reason })}\n\n`;
        default: {
            const exhaustive = frame;
            throw new Error(`unhandled frame ${JSON.stringify(exhaustive)}`);
        }
    }
}
export { encodeCursor, decodeCursor };
/**
 * Client-side cursor tracker.
 *
 * Keeps the last *rendered* cursor rather than the last received one, so a frame
 * that failed to apply is not later treated as seen.
 */
export class CursorTracker {
    #lastRendered = null;
    get cursor() {
        return this.#lastRendered;
    }
    /** Call only after the frame has actually been applied to the view. */
    markRendered(cursor) {
        this.#lastRendered = cursor;
    }
    /** Called when the server demands a reset; the old position becomes meaningless. */
    reset() {
        this.#lastRendered = null;
    }
}
//# sourceMappingURL=stream.js.map